/** Angles differ on purpose: thank-you sells order tracking, the order list sells reorder speed. */

import type { AppDeepLinkValue } from "./appLink";

export type AppPlacementId = "W1" | "W2" | "W3" | "W4" | "W5" | "W6" | "W7" | "W8";

/** OneLink pid per web placement; AppsFlyer reports these clicks as web banners. */
export const getAppPid = (placementId: AppPlacementId): string => `web-${placementId.toLowerCase()}`;

/** OneLink deep_link_value: W5 opens My Orders, every other placement the app home. */
export const getAppDeepLinkValue = (placementId: AppPlacementId): AppDeepLinkValue =>
  placementId === "W5" ? "orders" : "default";

export type AppPlacementTranslationCategory = "thankyou" | "my_account";

export interface AppPlacementConfig {
  id: AppPlacementId;
  /** Reported as `app_page`; analytics only, so it is safe to rename. */
  page: string;
  /** Dedupe key shared with the breakpoint twin; separate from `page` so renames never change dedupe. */
  surface: string;
  translationCategory: AppPlacementTranslationCategory;
  translationKeyPrefix: string;
}

const placement = (
  id: AppPlacementId,
  page: string,
  surface: string,
  translationCategory: AppPlacementTranslationCategory
): AppPlacementConfig => ({
  id,
  page,
  surface,
  translationCategory,
  translationKeyPrefix: `AppInstallBanner/${id}`,
});

export const APP_PLACEMENTS: Record<AppPlacementId, AppPlacementConfig> = {
  W1: placement("W1", "thankyou", "thankyou_banner", "thankyou"),
  W2: placement("W2", "thankyou", "thankyou_banner", "thankyou"),
  W3: placement("W3", "orders_list", "orders_list_banner", "my_account"),
  W4: placement("W4", "orders_list", "orders_list_banner", "my_account"),
  W5: placement("W5", "order_details", "order_details_banner", "my_account"),
  W6: placement("W6", "account_nav", "account_nav_row", "my_account"),
  W7: placement("W7", "sitewide", "sitewide_global_banner", "my_account"),
  W8: placement("W8", "treatment_plans", "treatment_plans_banner", "my_account"),
};

export type AppPlatform = "ios" | "android" | "desktop";

/** Analytics-only UA sniff; can disagree with the width-picked placement id, and both are correct. */
export const getAppPlatform = (): AppPlatform => {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/Android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  // iPadOS 13+ reports a desktop Safari UA and is only distinguishable by touch.
  if (/Macintosh/.test(ua) && (navigator as Navigator).maxTouchPoints > 1) return "ios";
  return "desktop";
};

/** The country these placements are scoped to. Country, not language: de-CH must not match. */
export const APP_PLACEMENT_COUNTRY = "de";

export const isAppPlacementCountry = (country?: string | null): boolean =>
  typeof country === "string" && country.toLowerCase() === APP_PLACEMENT_COUNTRY;
